import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowUpRight,
  Boxes,
  ChevronDown,
  Cloud,
  Compass,
  Download,
  FolderOpen,
  GitBranch,
  Layers3,
  LockKeyhole,
  LogOut,
  Menu,
  MousePointer2,
  Share2,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useAuthModal } from '@/contexts/AuthModalContext';
import { AuthModal } from '@/components/Auth/AuthModal';
import { ThemeToggle } from '@/components/Toolbar/ThemeToggle/ThemeToggle';
import {
  CheckList,
  FeatureIcon,
  MarketingButton,
  MarketingContainer,
  SectionHeader,
} from '@/components/Marketing';
import './landing.css';

const useCases = [
  { icon: Cloud, tone: 'blue' as const, title: 'Cloud architecture', description: 'Map AWS, Azure, and GCP systems with recognizable service icons.' },
  { icon: GitBranch, tone: 'violet' as const, title: 'DevOps workflows', description: 'Explain build, deployment, and delivery paths without visual noise.' },
  { icon: Boxes, tone: 'emerald' as const, title: 'Kubernetes systems', description: 'Show clusters, workloads, services, and their relationships in context.' },
  { icon: Layers3, tone: 'amber' as const, title: 'System design', description: 'Turn complex platform decisions into diagrams teams can discuss.' },
];

const cloudIcons = [
  '/icons/aws/analytics/athena-data-source-connectors.svg',
  '/icons/azure/compute/virtual-machine.svg',
  '/icons/gcp/ai/ai-machine-learning.svg',
  '/icons/kubernetes/resources/pod.svg',
];

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { openLogin, openSignup } = useAuthModal();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const startCreating = () => navigate('/editor');
  const explore = () => navigate('/explore');
  const myDiagrams = () => (user ? navigate('/my-diagrams') : openLogin('/my-diagrams'));

  useEffect(() => {
    const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
    if (!user && returnTo) {
      openLogin(returnTo);
      navigate('/', { replace: true, state: null });
    }
  }, [location.state, navigate, openLogin, user]);

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) setShowUserMenu(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowUserMenu(false);
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  return (
    <div className="marketing-page min-h-screen overflow-x-hidden bg-white text-slate-950 dark:bg-slate-950 dark:text-white">
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/85 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/85">
        <MarketingContainer className="flex h-16 items-center justify-between">
          <button onClick={() => navigate('/')} className="marketing-focus rounded-lg" aria-label="Degre120 home">
            <img src="/logo.svg" alt="Degre120" className="h-8 w-auto dark:brightness-0 dark:invert" />
          </button>

          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary navigation">
            <MarketingButton variant="ghost" onClick={startCreating}>Editor</MarketingButton>
            <MarketingButton variant="ghost" onClick={explore}>Explore</MarketingButton>
            <MarketingButton variant="ghost" onClick={myDiagrams}>My diagrams</MarketingButton>
          </nav>

          <div className="flex items-center gap-1.5">
            <ThemeToggle />
            {user ? (
              <div className="relative hidden sm:block" ref={userMenuRef}>
                <button
                  className="marketing-focus flex min-h-11 items-center gap-2 rounded-xl px-2.5 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800"
                  onClick={() => setShowUserMenu((open) => !open)}
                  aria-expanded={showUserMenu}
                  aria-haspopup="menu"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-xs text-white">
                    {user.email?.charAt(0).toUpperCase() || 'U'}
                  </span>
                  <span className="hidden max-w-28 truncate lg:block">{user.email?.split('@')[0]}</span>
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-xl dark:border-slate-700 dark:bg-slate-900" role="menu">
                    <p className="truncate border-b border-slate-100 px-3 py-2.5 text-xs text-slate-500 dark:border-slate-800">{user.email}</p>
                    <button className="marketing-focus mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800" onClick={myDiagrams} role="menuitem"><FolderOpen className="h-4 w-4" />My diagrams</button>
                    <button className="marketing-focus flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800" onClick={explore} role="menuitem"><Compass className="h-4 w-4" />Explore</button>
                    <button className="marketing-focus mt-1 flex w-full items-center gap-3 border-t border-slate-100 px-3 py-2.5 text-sm text-slate-600 hover:text-slate-950 dark:border-slate-800 dark:text-slate-300 dark:hover:text-white" onClick={async () => { await signOut(); setShowUserMenu(false); }} role="menuitem"><LogOut className="h-4 w-4" />Sign out</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden items-center gap-1 sm:flex">
                <MarketingButton variant="ghost" onClick={() => openLogin()}>Sign in</MarketingButton>
                <MarketingButton onClick={() => openSignup()} className="px-4 py-2">Sign up</MarketingButton>
              </div>
            )}
            <button
              className="marketing-focus flex h-11 w-11 items-center justify-center rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 md:hidden"
              onClick={() => setShowMobileMenu((open) => !open)}
              aria-label="Toggle navigation"
              aria-expanded={showMobileMenu}
            >
              {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </MarketingContainer>
        {showMobileMenu && (
          <MarketingContainer className="border-t border-slate-200 py-3 dark:border-slate-800 md:hidden">
            <nav className="grid gap-1" aria-label="Mobile navigation">
              {[['Editor', startCreating], ['Explore', explore], ['My diagrams', myDiagrams]].map(([label, handler]) => (
                <button key={label as string} className="marketing-focus rounded-lg px-3 py-3 text-left text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => { (handler as () => void)(); setShowMobileMenu(false); }}>{label as string}</button>
              ))}
              {!user && <div className="mt-2 grid grid-cols-2 gap-2 border-t border-slate-200 pt-3 dark:border-slate-800"><MarketingButton variant="secondary" onClick={() => openLogin()}>Sign in</MarketingButton><MarketingButton onClick={() => openSignup()}>Sign up</MarketingButton></div>}
              {user && <MarketingButton variant="secondary" className="mt-2" onClick={async () => { await signOut(); setShowMobileMenu(false); }}><LogOut className="h-4 w-4" />Sign out</MarketingButton>}
            </nav>
          </MarketingContainer>
        )}
      </header>

      <main>
        <section className="relative overflow-hidden border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
          <div className="marketing-grid absolute inset-0" aria-hidden="true" />
          <div className="absolute left-1/2 top-0 h-[32rem] w-[50rem] -translate-x-1/2 rounded-full bg-blue-300/25 blur-3xl dark:bg-blue-600/10" aria-hidden="true" />
          <MarketingContainer className="relative grid items-center gap-14 py-20 lg:grid-cols-[0.9fr_1.1fr] lg:py-28">
            <div className="max-w-2xl">
              <p className="marketing-eyebrow"><Sparkles className="mr-2 h-3.5 w-3.5" />Isometric infrastructure design</p>
              <h1 className="mt-7 text-5xl font-black leading-[1.02] tracking-[-0.045em] text-slate-950 dark:text-white sm:text-6xl lg:text-7xl">
                Make complex systems <span className="text-blue-600 dark:text-blue-400">clear at a glance.</span>
              </h1>
              <p className="mt-7 max-w-xl text-lg leading-8 text-slate-600 dark:text-slate-300 sm:text-xl">
                Create polished cloud architecture and DevOps diagrams with an isometric canvas and service icons your team already recognizes.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <MarketingButton onClick={startCreating} trailingArrow className="px-6 py-3.5 text-base">Start creating free</MarketingButton>
                <MarketingButton variant="secondary" onClick={explore} className="px-6 py-3.5 text-base"><Compass className="h-4 w-4" />Explore diagrams</MarketingButton>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-2"><LockKeyhole className="h-4 w-4 text-emerald-600" />No credit card</span>
                <span className="flex items-center gap-2"><Download className="h-4 w-4 text-blue-600" />PNG, JPEG, SVG & JSON</span>
              </div>
            </div>

            <div className="relative lg:pl-8">
              <div className="marketing-frame-glow overflow-hidden rounded-3xl border border-slate-200 bg-white p-2 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
                <div className="flex h-10 items-center gap-1.5 border-b border-slate-100 px-3 dark:border-slate-800"><span className="h-2.5 w-2.5 rounded-full bg-red-400" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" /><span className="ml-3 text-xs font-medium text-slate-400">Architecture workspace</span></div>
                <img src="/isometric-showcase.webp" alt="Isometric cloud architecture diagram created with Degre120" className="aspect-[4/3] w-full rounded-b-2xl object-cover" fetchPriority="high" />
              </div>
              <div className="absolute -bottom-5 -left-2 hidden items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-xl dark:border-slate-700 dark:bg-slate-900 sm:flex">
                <div className="flex -space-x-2">{cloudIcons.map((icon) => <span key={icon} className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-slate-50 p-2 dark:border-slate-900 dark:bg-slate-800"><img src={icon} alt="" className="h-full w-full" /></span>)}</div>
                <span className="pr-2 text-xs font-semibold text-slate-600 dark:text-slate-300">Cloud icon packs included</span>
              </div>
            </div>
          </MarketingContainer>
        </section>

        <section className="border-b border-slate-200 bg-white py-10 dark:border-slate-800 dark:bg-slate-950">
          <MarketingContainer className="grid grid-cols-2 gap-8 text-center lg:grid-cols-4">
            {[['AWS, Azure & GCP', 'Cloud providers'], ['Kubernetes', 'Infrastructure icons'], ['Isometric', 'Purpose-built canvas'], ['One click', 'Flexible exports']].map(([value, label]) => <div key={value}><p className="text-base font-bold text-slate-900 dark:text-white sm:text-lg">{value}</p><p className="mt-1 text-xs uppercase tracking-wider text-slate-500">{label}</p></div>)}
          </MarketingContainer>
        </section>

        <section className="py-20 sm:py-24">
          <MarketingContainer>
            <SectionHeader eyebrow="Built for technical storytelling" title="One canvas for the systems you explain every day" description="Keep the technical detail while giving stakeholders a picture they can understand." />
            <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {useCases.map(({ icon, tone, title, description }) => <article key={title} className="rounded-2xl border border-slate-200 bg-white p-6 transition hover:-translate-y-1 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900"><FeatureIcon icon={icon} tone={tone} /><h3 className="mt-5 text-lg font-bold">{title}</h3><p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{description}</p></article>)}
            </div>
          </MarketingContainer>
        </section>

        <section className="border-y border-slate-200 bg-slate-50 py-20 dark:border-slate-800 dark:bg-slate-900/35 sm:py-24">
          <MarketingContainer>
            <SectionHeader eyebrow="How it works" title="From idea to shareable diagram in three steps" />
            <div className="mt-14 grid gap-8 md:grid-cols-3">
              {[
                { icon: MousePointer2, number: '01', title: 'Choose your building blocks', text: 'Browse cloud, Kubernetes, and general-purpose icons from the component library.' },
                { icon: Layers3, number: '02', title: 'Compose the system', text: 'Place, connect, group, and label components on an isometric grid.' },
                { icon: Share2, number: '03', title: 'Share the story', text: 'Export a polished asset or save the diagram for continued collaboration.' },
              ].map(({ icon: Icon, number, title, text }) => <article key={number} className="relative rounded-2xl border border-slate-200 bg-white p-7 dark:border-slate-800 dark:bg-slate-900"><span className="text-xs font-bold tracking-widest text-blue-600 dark:text-blue-400">{number}</span><Icon className="mt-5 h-7 w-7" /><h3 className="mt-5 text-xl font-bold">{title}</h3><p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">{text}</p></article>)}
            </div>
          </MarketingContainer>
        </section>

        <section className="py-20 sm:py-28">
          <MarketingContainer className="grid items-center gap-14 lg:grid-cols-2">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 p-3 shadow-xl dark:border-slate-800 dark:bg-slate-900"><img src="/cta-background.webp" alt="Detailed isometric infrastructure diagram" className="aspect-[4/3] w-full rounded-2xl object-cover" loading="lazy" /></div>
            <div>
              <p className="marketing-eyebrow">Designed for clarity</p>
              <h2 className="mt-5 text-4xl font-extrabold tracking-tight sm:text-5xl">Technical depth without diagram clutter.</h2>
              <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-300">Degre120 combines visual structure with the details engineering teams need. Use layers, groups, connectors, labels, and official service imagery to build a shared source of understanding.</p>
              <CheckList className="mt-8" items={['Organize complex diagrams with groups and layers', 'Connect components with clear directional relationships', 'Use recognizable icons across major cloud platforms', 'Export at the format and quality your audience needs']} />
              <MarketingButton variant="ghost" onClick={explore} className="mt-7 px-0 text-blue-700 hover:bg-transparent dark:text-blue-300">See what others are building <ArrowUpRight className="h-4 w-4" /></MarketingButton>
            </div>
          </MarketingContainer>
        </section>

        <section className="pb-20 sm:pb-28">
          <MarketingContainer>
            <div className="relative overflow-hidden rounded-3xl bg-blue-700 px-6 py-14 text-center text-white shadow-2xl sm:px-12 sm:py-20">
              <div className="absolute inset-0 opacity-15"><img src="/cta-background.webp" alt="" className="h-full w-full object-cover" loading="lazy" /></div>
              <div className="relative mx-auto max-w-3xl"><Zap className="mx-auto h-8 w-8 text-blue-200" /><h2 className="mt-5 text-3xl font-extrabold tracking-tight sm:text-5xl">Give your architecture a clearer story.</h2><p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-blue-100">Open the canvas and turn your next system explanation into something people remember.</p><div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row"><MarketingButton onClick={startCreating} trailingArrow className="bg-white text-blue-800 hover:bg-blue-50">Start creating free</MarketingButton><MarketingButton onClick={explore} className="border border-white/30 bg-white/10 hover:bg-white/20">Browse diagrams</MarketingButton></div></div>
            </div>
          </MarketingContainer>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-slate-50 py-12 dark:border-slate-800 dark:bg-slate-950">
        <MarketingContainer className="flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
          <div><img src="/logo.svg" alt="Degre120" className="h-8 w-auto dark:brightness-0 dark:invert" /><p className="mt-4 max-w-md text-sm leading-6 text-slate-500">Isometric infrastructure diagrams for cloud architects, platform teams, and technical storytellers.</p></div>
          <nav className="flex flex-wrap gap-x-6 gap-y-3 text-sm font-medium text-slate-600 dark:text-slate-300" aria-label="Footer navigation"><button onClick={startCreating} className="hover:text-blue-600">Editor</button><button onClick={explore} className="hover:text-blue-600">Explore</button><button onClick={myDiagrams} className="hover:text-blue-600">My diagrams</button></nav>
        </MarketingContainer>
        <MarketingContainer className="mt-9 border-t border-slate-200 pt-6 text-xs text-slate-500 dark:border-slate-800">© {new Date().getFullYear()} Degre120. Built for clearer systems.</MarketingContainer>
      </footer>
      <AuthModal />
    </div>
  );
};
